import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { DocIds } from '@/api/types/backend-dto-types';
import { useDebounce } from '@/new/components/apideck/ApideckFilePickerCustom/utils/useDebounce';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/new/components/shadcn/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { cn } from '@/new/lib/utils';

interface DocumentSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  subsetId: string;
  taggedDocuments: { doc_id: string; file_name: string }[];
  onToggleDocument: (doc: DocIds) => void;
  onConfirm: () => void;
  onCancel: () => void;
  claimDocTypes: string[];
}

export function DocumentSelectionDialog({
  isOpen,
  onClose,
  subsetId,
  taggedDocuments,
  onToggleDocument,
  onConfirm,
  onCancel,
  claimDocTypes,
}: DocumentSelectionDialogProps) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: docIdsResponse, isLoading: isLoadingDocs } = useGetDocIds(matterId, subsetId, {
    limit: 1000,
    searchQuery: debouncedSearchQuery || undefined,
  });

  // Filter to only show claim-related document types
  const taggableDocs = useMemo(() => {
    const docs = docIdsResponse?.documents ?? [];
    return docs.filter((doc) => doc.type && claimDocTypes.includes(doc.type.toLowerCase()));
  }, [docIdsResponse?.documents, claimDocTypes]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--files" />
            Select Documents
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600">
              Select the documents you want to include in your summary. Only claim-related documents are available for
              this type of draft.
            </p>
            <div className="mt-2 text-xs text-gray-500">
              <strong>Available document types:</strong> {claimDocTypes.join(', ')}
            </div>
          </div>

          <Command className="rounded-lg border border-gray-200">
            <CommandInput placeholder="Search documents..." value={searchQuery} onValueChange={setSearchQuery} />
            <CommandList className="max-h-80 overflow-y-auto">
              <CommandEmpty>
                {isLoadingDocs
                  ? 'Loading documents...'
                  : debouncedSearchQuery
                    ? `No claim-related documents found matching "${debouncedSearchQuery}"`
                    : 'No claim-related documents found'}
              </CommandEmpty>
              <CommandGroup>
                {taggableDocs.map((doc) => {
                  const isSelected = taggedDocuments.some((taggedDoc) => taggedDoc.doc_id === doc.id);
                  return (
                    <CommandItem
                      key={doc.id}
                      value={doc.file.name}
                      onSelect={() => onToggleDocument(doc)}
                      className={cn('cursor-pointer', isSelected && 'border-l-4 border-teal-500 bg-teal-50/50')}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'flex size-5 items-center justify-center rounded border-2 transition-colors',
                            isSelected
                              ? 'border-teal-500 bg-teal-500 text-white'
                              : 'border-gray-300 bg-white hover:border-gray-400',
                          )}
                        >
                          {isSelected && <Icon className="lucide--check size-3" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{doc.file.name}</div>
                          <div className="mt-1 text-xs text-gray-500">Type: {doc.type}</div>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>

          <div className="flex items-center justify-between border-t pt-4">
            <div className="text-sm text-gray-600">
              <Icon className="lucide--file-check mr-1 inline size-4" />
              {taggedDocuments.length} document{taggedDocuments.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onConfirm} disabled={taggedDocuments.length === 0}>
                <Icon className="lucide--check" />
                Done
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
